<template>
  <div class="is-relative" style="min-height: 8rem">
    <div v-if="isError" class="message is-danger">
      <div class="message-body">
        An error occured: <code>{{ error }}</code>
      </div>
    </div>
    <header style="margin-bottom: 0.75rem">
      <div v-if="itemsTotal > 0" style="margin-bottom: 0.75rem">
        <strong>{{ itemsTotal }} {{ $t("Pages found") }}</strong>
      </div>
      <b-pagination
        v-if="pagesTotal > 1"
        :total="itemsTotal"
        :current.sync="currentPage"
        :range-before="3"
        :range-after="4"
        order="is-right"
        :per-page="itemsPerPage"
        icon-prev="chevron-left"
        icon-next="chevron-right"
      />
    </header>
    <div v-show="!isLoading && itemsTotal === 0" class="message is-dark">
      <div class="message-body">
        {{ $t("No results for") }}
        <strong class="is-italic">{{ query.term }}</strong>
      </div>
    </div>
    <div v-if="itemsTotal > 0" style="margin-bottom: 0.75rem">
      <SearchResultList>
        <SearchResultListItem
          v-for="(searchResult, index) in results"
          :key="index"
          :route="searchResultRoutes[index]"
          style="margin-bottom: 0.75rem"
        >
          <template #header>
            {{ searchResult.title }} - {{ $t("Page") }} {{ searchResult.page }}
          </template>
          <SearchResultSnippet
            :data="searchResult.snippets"
            :searchResult="searchResult"
          />
        </SearchResultListItem>
      </SearchResultList>
    </div>
    <footer>
      <b-pagination
        v-if="pagesTotal > 1"
        :total="itemsTotal"
        :current.sync="currentPage"
        :range-before="3"
        :range-after="4"
        order="is-right"
        :per-page="itemsPerPage"
        icon-prev="chevron-left"
        icon-next="chevron-right"
      />
    </footer>
    <b-loading :active="isLoading" :is-full-page="false"></b-loading>
  </div>
</template>

<script>
import SearchResultList from "@/components/SearchResultList.vue";
import SearchResultListItem from "@/components/SearchResultListItem.vue";
import SearchResultSnippet from "@/components/SearchResultSnippet.vue";
import SearchResultContext from "@/components/SearchResultContext.vue";
import SearchView from "@/components/SearchView.vue";

import { debounce } from "@/utils/misc.js";
import imageMixin from "@/mixins/image.js";

import { createValidator } from "@/utils/validation.js";
import { createMapper } from "@/utils/mapping.js";

import api from "@/api/index.js";

import config from "@/config.json";

const validator = createValidator("search-form");
const mapper = createMapper("search-form");

export default {
  components: {
    SearchResultList,
    SearchResultListItem,
    SearchResultSnippet,
    SearchResultContext,
    SearchView,
  },
  mixins: [imageMixin],
  props: {
    order: {
      type: Array,
      default: () => [],
    },
  },
  watch: {
    currentPage: {
      handler: function () {
        const query = { ...this.$route.query };
        query[mapper.toURL("page")] = this.currentPage;
        this.$router.push({ query });
      },
    },
    viewMode: {
      handler(value) {
        if (value === validator.getDefault("view")) {
          const { view, ...query } = mapper.fromURL(this.$route.query);

          this.$router.push({
            ...this.$route,
            query: mapper.toURL(query),
          });
        } else {
          const query = mapper.toURL({ view: value });

          this.$router.push({
            ...this.$route,
            query: {
              ...this.$route.query,
              ...query,
            },
          });
        }
      },
      immediate: false,
    },

    "$route.query": {
      handler: "update",
      // NOTE: should NOT run before children's watchers
      immediate: false,
    },
  },
  data() {
    return {
      isLoading: false,
      viewMode: "combined",
      error: null,
      currentPage: 1,
      itemsPerPage: 10,
      total: NaN,
      results: [],
      rangeValues: [],
      filterFacets: [],
      facets: [],
      sortDirection: "asc",
    };
  },
  computed: {
    isError() {
      return this.error !== null;
    },
    canLoad() {
      return !this.isLoading && validator.isValid(this.query);
    },
    metaFields() {
      return config.meta;
    },
    query() {
      const { page, view, ...query } = mapper.fromURL(this.$route.query);
      if (validator.isValid(query)) {
        return validator.clean(query);
      } else {
        return {};
      }
    },
    searchResultRoutes() {
      if (this.query.term) {
        let term = this.query.term.trim().replaceAll(" OR", "");

        return this.results.map((result) => ({
          path: `/document/${result.id}/pages/${result.page}`,
          query: mapper.toURL({
            term: term,
          }),
        }));
      }
    },
    rangeFields() {
      return config.range;
    },
    itemsTotal() {
      if (!Number.isInteger(this.total)) {
        return null;
      } else {
        return this.total;
      }
    },
    pagesTotal() {
      return Math.ceil(this.total / this.itemsPerPage);
    },
    offset() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    limit() {
      return this.itemsPerPage;
    },
    params() {
      return { offset: this.offset, limit: this.limit };
    },
  },
  async mounted() {
    await this.tryToLoad();
  },
  methods: {
    setSort(sortField) {
      if (this.sortDirection === "asc") {
        this.sortDirection = "desc";
      } else {
        this.sortDirection = "asc";
      }
      const query = { ...this.$route.query };
      // if(sortField === 'relevance'){

      // }
      query["sortDir"] = this.sortDirection;
      query["sortField"] = sortField;
      this.$router.push({ query });
    },
    getSortIcon(value){
            const query = { ...this.$route.query };
            if( query["sortField"] === value){
              if(query["sortDir"] === 'asc'){
                return 'sort-ascending'
              }else{
                return 'sort-descending'
              }
            }

      return 'swap-vertical'
    },
    update(query, prevQuery = {}) {
      let mustReload = false;
      let mustResetPage = true;
      let mustReset = false;

      if (query["p"] != prevQuery["p"]) {
        mustReload = true;
        mustResetPage = false;
      }
      if (query["x"] != prevQuery["x"]) {
        mustReload = true;
        mustResetPage = false;
      }
      if (query["t"] != prevQuery["t"]) {
        mustReload = true;
        mustResetPage = false;
      }

      if (query["f"] != prevQuery["f"]) {
        mustReload = true;
        mustResetPage = false;
      }
      // do not reload anything if view changed
      // if (query["view"] != prevQuery["view"]) {
      //   this.viewMode = query["view"];
      //   if (mustReload === false && mustReset === false) {
      //     mustResetPage = false;
      //   }
      // }

      for (var meta in this.metaFields) {
        var field = this.metaFields[meta];
        if (query[field] != prevQuery[field]) {
          this.currentPage = 1;
          mustReload = true;
          var obj = { value: query[field], field: field };
          if (query[field]) {
            this.filterFacets.push(obj);
          }
        }
      }

      for (var range in this.rangeFields) {
        var entry = this.rangeFields[range];
        if (!this.compareArray(query[entry.field], prevQuery[entry.field])) {
          mustReload = true;
          this.currentPage = 1;

          if (query[entry.field]) {
            var obj = {
              field: entry.field,
              from: query[entry.field][0],
              to: query[entry.field][1],
            };
            this.rangeValues.push(obj);
          }
        }
      }

      const parsedValue = parseInt(query["p"]);
      if (Number.isFinite(parsedValue)) {
        if (!Number.isFinite(this.pagesTotal)) {
          this.currentPage = parsedValue;
        } else {
          this.currentPage = Math.max(
            1,
            Math.min(this.pagesTotal, parsedValue)
          );
        }
      }

      if (mustReload) {
        this.tryToLoad();
      }
    },
    async tryToLoad() {
      if (this.canLoad) {
        await this.load();
      }
    },
    async getWidthHeight(result) {
      let imageInfo = this.getImageInfo(result.image.id);
      let response = await fetch(imageInfo)
        .then((response) => response.json())
        .then((data) => {
          result.image.width = data.width;
          result.image.height = data.height;
        });
    },
    compareArray(array1, array2) {
      if (array1 && array2) {
        return (
          array1.length === array2.length &&
          array1.every((value, index) => value === array2[index])
        );
      }
    },
    load: debounce(async function load() {
      this.isLoading = true;
      const query = { ...this.$route.query };
      this.rangeValues = [];
      this.filterFacets = [];

      for (var meta in this.metaFields) {
        var field = this.metaFields[meta];
        var obj = { value: query[field], field: field };
        if (query[field]) {
          this.filterFacets.push(obj);
        }
      }

      for (var range in this.rangeFields) {
        var entry = this.rangeFields[range];
        if (query[entry.field]) {
          var obj = null;
          if (entry.type == "date") {
            if (query[entry.field][0]) {
              obj = {
                field: entry.field,
                from: query[entry.field][0] + "-01-01T01:00:00Z",
                to: query[entry.field][1] + "-12-30T01:00:00Z",
              };
            }
          }
          if (obj) {
            this.rangeValues.push(obj);
          }
        }
      }

      const { total, results, facets } = await this.fetchNextPage();
      this.total = total;
      this.facets = facets;
      this.$emit("setFacets", this.facets);
      results.map(this.getWidthHeight);
      this.results = results;
      this.isLoading = false;
    }, 50),
    async fetchNextPage() {
      const query = { ...this.$route.query };

      if (this.error !== null) {
        this.error = null;
      }
      try {
        return await api.fetchSearchResults(
          {
            range: this.rangeValues,
            facetArr: this.metaFields,
            filterFacets: this.filterFacets,
            sortField: query["sortField"],
            sortDirection: this.sortDirection,
            ...this.query,
          },
          this.params
        );
      } catch (err) {
        this.error = err.message;
      }
    },
  },
};
</script>
